import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, Tag, DollarSign, Ticket, X, Check } from 'lucide-react';

const emptyPromo = { code: '', discount_percent: 10, active: true, expires_at: '', max_uses: '', description: '' };

export default function AdminPricingManager() {
  const [loading, setLoading] = useState(true);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [newPromo, setNewPromo] = useState(emptyPromo);
  const [creatingPromo, setCreatingPromo] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, promoRes] = await Promise.all([
        adminAPI.getPricing(),
        adminAPI.getPromoCodes().catch(() => ({ data: [] })),
      ]);
      setPricing(pRes.data);
      setPromoCodes(promoRes.data || []);
    } catch {
      toast.error('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (key, value) => setPricing((prev) => ({ ...prev, [key]: value }));

  const updateFeature = (idx, value) => {
    setPricing((prev) => {
      const features = [...(prev.features || [])];
      features[idx] = value;
      return { ...prev, features };
    });
  };
  const addFeature = () => setPricing((prev) => ({ ...prev, features: [...(prev.features || []), ''] }));
  const removeFeature = (idx) => setPricing((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const payload = {
        currency: pricing.currency || 'usd',
        monthly_amount: parseFloat(pricing.monthly_amount),
        annual_amount: parseFloat(pricing.annual_amount),
        monthly_tagline: pricing.monthly_tagline,
        annual_tagline: pricing.annual_tagline,
        annual_badge: pricing.annual_badge,
        features: (pricing.features || []).filter((f) => f && f.trim()),
        guarantee_text: pricing.guarantee_text,
      };
      const { data } = await adminAPI.updatePricing(payload);
      setPricing(data.pricing);
      toast.success('Pricing updated — synced to landlord & landing pages');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleCreatePromo = async () => {
    if (!newPromo.code.trim()) { toast.error('Enter a code'); return; }
    const pct = parseInt(newPromo.discount_percent, 10);
    if (!(pct >= 1 && pct <= 100)) { toast.error('Discount must be 1–100%'); return; }
    setCreatingPromo(true);
    try {
      const payload = {
        code: newPromo.code.trim().toUpperCase(),
        discount_percent: pct,
        active: newPromo.active,
        expires_at: newPromo.expires_at ? new Date(newPromo.expires_at).toISOString() : null,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses, 10) : null,
        description: newPromo.description || null,
      };
      await adminAPI.createPromoCode(payload);
      toast.success('Promo code created');
      setPromoDialogOpen(false);
      setNewPromo(emptyPromo);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleToggleActive = async (promo) => {
    try {
      await adminAPI.updatePromoCode(promo.promo_id, { active: !promo.active });
      setPromoCodes((prev) => prev.map((p) => (p.promo_id === promo.promo_id ? { ...p, active: !p.active } : p)));
    } catch (err) {
      toast.error('Failed to update promo code');
    }
  };

  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deletePromoCode(promo.promo_id);
      setPromoCodes((prev) => prev.filter((p) => p.promo_id !== promo.promo_id));
      toast.success('Promo code deleted');
    } catch {
      toast.error('Failed to delete promo code');
    }
  };

  if (loading || !pricing) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const monthly = parseFloat(pricing.monthly_amount) || 0;
  const annual = parseFloat(pricing.annual_amount) || 0;
  const savingsPct = monthly > 0 ? Math.round((1 - annual / (monthly * 12)) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-pricing-manager">
      {/* Plan Pricing */}
      <Card data-testid="pricing-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#C85A42]" />
            Plan Pricing
          </CardTitle>
          <CardDescription>
            One unlimited plan, billed monthly or annually. Changes here sync instantly to the landlord subscription page and the public landing page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monthly */}
            <div className="space-y-3 rounded-lg border p-4">
              <h3 className="font-semibold">Monthly Plan</h3>
              <div className="space-y-2">
                <Label>Price (USD)</Label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="number" step="0.01" min="0"
                    className="pl-9"
                    value={pricing.monthly_amount}
                    onChange={(e) => updateField('monthly_amount', e.target.value)}
                    data-testid="pricing-monthly-amount"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input
                  value={pricing.monthly_tagline || ''}
                  onChange={(e) => updateField('monthly_tagline', e.target.value)}
                  data-testid="pricing-monthly-tagline"
                />
              </div>
            </div>

            {/* Annual */}
            <div className="space-y-3 rounded-lg border p-4 border-[#C85A42]/40">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Annual Plan</h3>
                {savingsPct > 0 && <Badge className="bg-[#C85A42] text-white">Save ~{savingsPct}%</Badge>}
              </div>
              <div className="space-y-2">
                <Label>Price (USD)</Label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="number" step="0.01" min="0"
                    className="pl-9"
                    value={pricing.annual_amount}
                    onChange={(e) => updateField('annual_amount', e.target.value)}
                    data-testid="pricing-annual-amount"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input
                  value={pricing.annual_tagline || ''}
                  onChange={(e) => updateField('annual_tagline', e.target.value)}
                  data-testid="pricing-annual-tagline"
                />
              </div>
              <div className="space-y-2">
                <Label>Badge text</Label>
                <Input
                  value={pricing.annual_badge || ''}
                  onChange={(e) => updateField('annual_badge', e.target.value)}
                  data-testid="pricing-annual-badge"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Features (shown on both plans)</Label>
              <Button variant="outline" size="sm" onClick={addFeature} data-testid="add-feature-btn">
                <Plus className="w-4 h-4 mr-1" /> Add feature
              </Button>
            </div>
            <div className="space-y-2">
              {(pricing.features || []).map((f, idx) => (
                <div key={`feature-${idx}`} className="flex items-center gap-2">
                  <Input
                    value={f}
                    onChange={(e) => updateFeature(idx, e.target.value)}
                    placeholder="e.g. Unlimited properties & units"
                    data-testid={`feature-input-${idx}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeFeature(idx)} data-testid={`remove-feature-${idx}`}>
                    <X className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Guarantee text</Label>
            <Textarea
              value={pricing.guarantee_text || ''}
              onChange={(e) => updateField('guarantee_text', e.target.value)}
              rows={2}
              data-testid="pricing-guarantee-text"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSavePricing} disabled={savingPricing} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="save-pricing-btn">
              {savingPricing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Pricing
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Promo Codes */}
      <Card data-testid="promo-codes-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#2F4F4F]" />
              Promotional Codes
            </CardTitle>
            <CardDescription>Percentage-off codes landlords can apply at checkout.</CardDescription>
          </div>
          <Button onClick={() => { setNewPromo(emptyPromo); setPromoDialogOpen(true); }} className="bg-[#2F4F4F] hover:bg-[#1a3a3a]" data-testid="new-promo-btn">
            <Plus className="w-4 h-4 mr-1" /> New Code
          </Button>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-8" data-testid="promo-empty">
              No promo codes yet. Create one to offer a discount at checkout.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promoCodes.map((p) => (
                    <TableRow key={p.promo_id} data-testid={`promo-row-${p.code}`}>
                      <TableCell className="font-mono font-semibold">{p.code}</TableCell>
                      <TableCell>{p.discount_percent}% off</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {p.used_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ''}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Switch checked={!!p.active} onCheckedChange={() => handleToggleActive(p)} data-testid={`promo-toggle-${p.code}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDeletePromo(p)} data-testid={`promo-delete-${p.code}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Promo Dialog */}
      <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
        <DialogContent data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle>New Promo Code</DialogTitle>
            <DialogDescription>Landlords enter this code at checkout to get a percentage discount.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                value={newPromo.code}
                onChange={(e) => setNewPromo((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. LAUNCH20"
                className="font-mono"
                data-testid="promo-code-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number" min="1" max="100"
                  value={newPromo.discount_percent}
                  onChange={(e) => setNewPromo((p) => ({ ...p, discount_percent: e.target.value }))}
                  data-testid="promo-discount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Max uses (optional)</Label>
                <Input
                  type="number" min="1"
                  value={newPromo.max_uses}
                  onChange={(e) => setNewPromo((p) => ({ ...p, max_uses: e.target.value }))}
                  placeholder="Unlimited"
                  data-testid="promo-maxuses-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expiry date (optional)</Label>
              <Input
                type="date"
                value={newPromo.expires_at}
                onChange={(e) => setNewPromo((p) => ({ ...p, expires_at: e.target.value }))}
                data-testid="promo-expiry-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={newPromo.description}
                onChange={(e) => setNewPromo((p) => ({ ...p, description: e.target.value }))}
                placeholder="Internal note"
                data-testid="promo-desc-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={newPromo.active} onCheckedChange={(v) => setNewPromo((p) => ({ ...p, active: v }))} data-testid="promo-active-switch" />
              <Label className="cursor-pointer">Active immediately</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePromo} disabled={creatingPromo} className="bg-[#2F4F4F] hover:bg-[#1a3a3a]" data-testid="create-promo-btn">
              {creatingPromo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
